import { accountDisplayName } from './account-display';

/**
 * Calculated label for an exterior improvement application (#363):
 *
 *   <Account Label>: YYYY-MM-DD - <Request Type>
 *   8580OF - Ponder: 2026-07-28 - Adding Fence
 *
 * The account half reuses `accountDisplayName()` — the same "3277NL - Frere" the
 * account page renders — so one convention identifies a lot everywhere.
 *
 * The owner is the LOT'S owner of record, not the submitter; those differ whenever
 * someone files on the owner's behalf, or a unit changed hands and our records
 * haven't caught up.
 *
 * The date is ISO (UTC) so labels sort chronologically as plain strings, which is
 * also what makes them safe to use verbatim as document folder names. Formatted from UTC
 * getters deliberately: a local-time format would shift a late-evening submission
 * onto the following day.
 *
 * Each segment is omitted rather than rendered empty, so a partially-known
 * application still produces something readable instead of stray separators.
 */
export interface ApplicationTitleFields {
	accountId?: string | null;
	accountNumber?: string | null;
	accountOwnerName?: string | null;
	accountAddress?: string | null;
	requestTypeName?: string | null;
	applicationNumber?: number | null;
	/** ISO string or epoch ms — the created/submitted date. */
	createdAt?: string | number | null;
}

/** YYYY-MM-DD in UTC, or '' when the value is missing/unparseable. */
export function labelDate(value?: string | number | null): string {
	if (value === null || value === undefined || value === '') return '';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '';
	const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
	const dd = String(d.getUTCDate()).padStart(2, '0');
	return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

export function applicationDisplayTitle(app: ApplicationTitleFields): string {
	const type = app.requestTypeName?.trim() || 'Request';
	const date = labelDate(app.createdAt);

	// Unmatched: no lot to name, so the number is the only identity available.
	const account = app.accountId
		? accountDisplayName({
			accountNumber: app.accountNumber ?? null,
			primaryOwnerName: app.accountOwnerName ?? null,
			// Fall back to the lot address when no owner is on record — more use to a
			// staffer scanning a list than a bare shortcode.
			primaryOwnerCompanyName: app.accountOwnerName ? null : (app.accountAddress ?? null),
			// No `id` fallback: an 8-char uuid slice is noise in a label.
		})
		: (app.applicationNumber != null ? `#${app.applicationNumber}` : '');

	// "<account>: <date>" then " - <type>", skipping whichever parts are unknown.
	const head = [account, date].filter(Boolean).join(': ');
	return [head, type].filter(Boolean).join(' - ');
}
